import React, { useState, useEffect } from 'react';
import { X, Table, RefreshCw, Send, CheckCircle2, Clock, ExternalLink, Download, MessageSquare, Copy, Check } from 'lucide-react';
import { OrderRecord, OrderStatus } from '../types';
import { formatThaiDateTime } from '../lib/dateUtils';

interface GoogleSheetsAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectOrderForSlip: (order: OrderRecord) => void;
}

export const GoogleSheetsAdminModal: React.FC<GoogleSheetsAdminModalProps> = ({
  isOpen,
  onClose,
  onSelectOrderForSlip,
}) => {
  const [activeTab, setActiveTab] = useState<'orders' | 'items' | 'linePreview'>('orders');
  const [orders, setOrders] = useState<OrderRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [copiedLine, setCopiedLine] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [selectedOrderForLine, setSelectedOrderForLine] = useState<OrderRecord | null>(null);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/orders');
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders || []);
        if (data.orders?.length > 0 && !selectedOrderForLine) {
          setSelectedOrderForLine(data.orders[0]);
        }
      }
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchOrders();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleUpdateStatus = async (orderId: string, newStatus: OrderStatus) => {
    try {
      setUpdatingId(orderId);
      const res = await fetch(`/api/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderStatus: newStatus,
          paymentStatus: newStatus === 'Completed' || newStatus === 'Shipped' ? 'Paid' : undefined,
          trackingNumber: newStatus === 'Shipped' ? `TH${Date.now().toString().slice(-9)}` : undefined,
        }),
      });
      if (res.ok) {
        await fetchOrders();
      }
    } catch (err) {
      console.error('Status update failed:', err);
    } finally {
      setUpdatingId(null);
    }
  };

  // CSV Export for Google Sheets
  const handleExportCsv = () => {
    if (activeTab === 'orders') {
      const headers = [
        'Order_ID',
        'Timestamp',
        'Customer_Name',
        'Phone',
        'Address',
        'Total_Amount',
        'Payment_Method',
        'Payment_Status',
        'Slip_URL',
        'Order_Status',
        'Tracking_Number',
        'LINE_User_ID',
      ];
      const rows = orders.map((o) => [
        `"${o.orderId}"`,
        `"${formatThaiDateTime(o.timestamp)}"`,
        `"${o.customerName}"`,
        `"${o.phone}"`,
        `"${o.address.replace(/"/g, '""')}"`,
        o.totalAmount,
        `"${o.paymentMethod}"`,
        `"${o.paymentStatus}"`,
        `"${o.slipUrl || ''}"`,
        `"${o.orderStatus}"`,
        `"${o.trackingNumber || ''}"`,
        `"${o.lineUserId || ''}"`,
      ]);
      const csvContent = '\uFEFF' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
      downloadFile(csvContent, 'GoogleSheets_Orders.csv');
    } else {
      const headers = [
        'Order_ID',
        'Product_Code',
        'Product_Name',
        'Quantity',
        'Price_Per_Unit',
        'Subtotal',
      ];
      const rows: string[] = [];
      orders.forEach((o) => {
        o.items.forEach((item) => {
          rows.push(
            [
              `"${o.orderId}"`,
              `"${item.productCode}"`,
              `"${item.productName.replace(/"/g, '""')}"`,
              item.quantity,
              item.pricePerUnit,
              item.subtotal,
            ].join(',')
          );
        });
      });
      const csvContent = '\uFEFF' + [headers.join(','), ...rows].join('\n');
      downloadFile(csvContent, 'GoogleSheets_Order_Items.csv');
    }
  };

  const downloadFile = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-xs p-3 sm:p-6 overflow-y-auto">
      <div className="relative w-full max-w-5xl my-auto bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200 flex flex-col max-h-[90vh]">
        {/* Header - Deep Navy Blue (#0E2C58 / #1E3A8A) for Admin/Backend Modals */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-blue-900 bg-gradient-to-r from-[#0E2C58] to-[#1E3A8A] text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 border border-white/20 flex items-center justify-center text-white shadow-xs">
              <Table className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-white tracking-tight">
                  ฐานข้อมูล Google Sheets & ระบบหลังบ้านกิฟฟารีน
                </h2>
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 font-semibold">
                  API v4 Schema
                </span>
              </div>
              <p className="text-xs text-blue-100/80">
                สถาปัตยกรรม Orders & Order_Items พร้อมจำลองการแจ้งเตือน LINE Messaging API
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={fetchOrders}
              className="p-2 text-blue-100 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
              title="รีเฟรชข้อมูล"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-blue-200 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tab Controls & Actions */}
        <div className="px-6 py-3 border-b border-slate-200 bg-white flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 p-1 bg-slate-100 rounded-xl">
            <button
              onClick={() => setActiveTab('orders')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                activeTab === 'orders'
                  ? 'bg-white text-blue-700 shadow-xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Tab: Orders ({orders.length})
            </button>
            <button
              onClick={() => setActiveTab('items')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                activeTab === 'items'
                  ? 'bg-white text-blue-700 shadow-xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Tab: Order_Items
            </button>
            <button
              onClick={() => setActiveTab('linePreview')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                activeTab === 'linePreview'
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'text-slate-600 hover:text-emerald-700'
              }`}
            >
              <MessageSquare className="w-3.5 h-3.5" />
              LINE Flex Message
            </button>
          </div>

          <button
            onClick={handleExportCsv}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-xl text-xs font-medium transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            ส่งออก CSV (สำหรับนำเข้า Google Sheets)
          </button>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-auto p-4 sm:p-6 bg-slate-50/50">
          {activeTab === 'orders' && (
            <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-xs">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-100/80 text-slate-600 border-b border-slate-200 font-semibold">
                    <tr>
                      <th className="p-3">Order_ID</th>
                      <th className="p-3">Timestamp</th>
                      <th className="p-3">Customer_Name</th>
                      <th className="p-3">Phone</th>
                      <th className="p-3">Total_Amount</th>
                      <th className="p-3">Payment_Method</th>
                      <th className="p-3">Payment_Status</th>
                      <th className="p-3">Slip</th>
                      <th className="p-3">Order_Status</th>
                      <th className="p-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {orders.map((o) => (
                      <tr key={o.orderId} className="hover:bg-slate-50/80 transition-colors">
                        <td className="p-3 font-mono font-bold text-blue-700">
                          {o.orderId}
                        </td>
                        <td className="p-3 text-slate-500 font-mono text-[11px] whitespace-nowrap">
                          {formatThaiDateTime(o.timestamp)}
                        </td>
                        <td className="p-3 font-medium text-slate-800">
                          {o.customerName}
                        </td>
                        <td className="p-3 font-mono text-slate-600">{o.phone}</td>
                        <td className="p-3 font-bold text-slate-900">
                          ฿{o.totalAmount.toLocaleString()}
                        </td>
                        <td className="p-3">
                          <span
                            className={`px-2 py-0.5 rounded-md text-[10.5px] font-semibold ${
                              o.paymentMethod === 'BANK_TRANSFER'
                                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                : 'bg-amber-50 text-amber-700 border border-amber-200'
                            }`}
                          >
                            {o.paymentMethod === 'BANK_TRANSFER' ? 'โอนเงิน' : 'COD'}
                          </span>
                        </td>
                        <td className="p-3">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                              o.paymentStatus === 'Paid'
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-amber-50 text-amber-700'
                            }`}
                          >
                            {o.paymentStatus}
                          </span>
                        </td>
                        <td className="p-3">
                          {o.slipUrl ? (
                            <a
                              href={o.slipUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-600 hover:underline flex items-center gap-1 text-[11px]"
                            >
                              ดูสลิป <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </td>
                        <td className="p-3">
                          <select
                            value={o.orderStatus}
                            onChange={(e) =>
                              handleUpdateStatus(o.orderId, e.target.value as OrderStatus)
                            }
                            disabled={updatingId === o.orderId}
                            className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 focus:border-blue-500 focus:outline-none"
                          >
                            <option value="Wait_Verification">Wait_Verification</option>
                            <option value="Pending_Shipment">Pending_Shipment</option>
                            <option value="Processing">Processing</option>
                            <option value="Shipped">Shipped</option>
                            <option value="Completed">Completed</option>
                          </select>
                        </td>
                        <td className="p-3 text-right space-x-1 whitespace-nowrap">
                          <button
                            onClick={() => {
                              setSelectedOrderForLine(o);
                              setActiveTab('linePreview');
                            }}
                            className="px-2 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded text-[11px] font-medium"
                          >
                            ดู LINE
                          </button>
                          <button
                            onClick={() => {
                              onSelectOrderForSlip(o);
                              onClose();
                            }}
                            className="px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded text-[11px] font-medium"
                          >
                            ดูสลิปใบเสร็จ
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'items' && (
            <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-xs">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-100/80 text-slate-600 border-b border-slate-200 font-semibold">
                    <tr>
                      <th className="p-3">Order_ID</th>
                      <th className="p-3">Product_Code</th>
                      <th className="p-3">Product_Name</th>
                      <th className="p-3">Quantity</th>
                      <th className="p-3">Price_Per_Unit</th>
                      <th className="p-3">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {orders.map((o) =>
                      o.items.map((item, idx) => (
                        <tr key={`${o.orderId}-${idx}`} className="hover:bg-slate-50/80">
                          <td className="p-3 font-mono font-bold text-blue-700">
                            {o.orderId}
                          </td>
                          <td className="p-3 font-mono text-slate-600">
                            {item.productCode}
                          </td>
                          <td className="p-3 font-medium text-slate-800">
                            {item.productName}
                          </td>
                          <td className="p-3 font-bold">{item.quantity}</td>
                          <td className="p-3 text-slate-600">
                            ฿{item.pricePerUnit.toLocaleString()}
                          </td>
                          <td className="p-3 font-bold text-slate-900">
                            ฿{item.subtotal.toLocaleString()}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'linePreview' && selectedOrderForLine && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
              {/* Phone Mockup with LINE Bubble */}
              <div className="flex flex-col items-center">
                <div className="text-xs font-semibold text-slate-500 mb-2">
                  ตัวอย่าง LINE Flex Message ที่ส่งเข้า LINE Admin / ลูกค้า
                </div>
                <div className="w-full max-w-[320px] bg-slate-900 p-3 rounded-[32px] shadow-2xl border-4 border-slate-700">
                  <div className="bg-[#8C9DAE] rounded-[24px] p-3 min-h-[460px] flex flex-col justify-start">
                    {/* Line Header */}
                    <div className="text-center text-[10px] text-white/90 mb-2 font-medium">
                      Giffarine Notification Bot
                    </div>

                    {/* LINE Flex Bubble */}
                    <div className="bg-white rounded-2xl shadow-md overflow-hidden text-slate-800 text-xs">
                      <div className="bg-[#4B7BEC] p-3 text-white">
                        <span className="text-[9px] font-bold tracking-widest block opacity-90">
                          GIFFARINE ONLINE
                        </span>
                        <h4 className="text-sm font-extrabold mt-0.5">
                          ✓ คำสั่งซื้อใหม่สำเร็จ!
                        </h4>
                        <p className="text-[10px] text-blue-100 font-mono">
                          {selectedOrderForLine.orderId}
                        </p>
                      </div>

                      <div className="p-3 space-y-2 text-[11px]">
                        <div className="flex justify-between">
                          <span className="text-slate-400">ลูกค้า:</span>
                          <span className="font-semibold text-slate-900">
                            {selectedOrderForLine.customerName}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">โทร:</span>
                          <span className="font-mono">{selectedOrderForLine.phone}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">วิธีชำระ:</span>
                          <span className="font-bold text-blue-700">
                            {selectedOrderForLine.paymentMethod === 'BANK_TRANSFER' ? 'โอนเงิน (แนบสลิป)' : 'เก็บเงินปลายทาง (COD)'}
                          </span>
                        </div>

                        <div className="border-t border-slate-100 pt-1.5">
                          <span className="text-[10px] font-bold text-slate-400 block mb-1">
                            รายการสินค้า:
                          </span>
                          {selectedOrderForLine.items.map((it, idx) => (
                            <div key={idx} className="flex justify-between text-[10.5px]">
                              <span className="truncate pr-2">• {it.productName}</span>
                              <span className="shrink-0 font-medium">
                                x{it.quantity} (฿{it.subtotal})
                              </span>
                            </div>
                          ))}
                        </div>

                        <div className="border-t border-slate-800 pt-2 flex justify-between items-baseline font-bold text-slate-900">
                          <span>ยอดรวมสุทธิ:</span>
                          <span className="text-sm text-blue-600 font-mono">
                            ฿{selectedOrderForLine.totalAmount.toLocaleString()}
                          </span>
                        </div>
                      </div>

                      {/* Action Button inside Flex */}
                      <div className="p-2 bg-slate-50 border-t border-slate-100">
                        <button
                          onClick={() => onSelectOrderForSlip(selectedOrderForLine)}
                          className="w-full py-1.5 bg-[#4B7BEC] text-white rounded-lg text-center text-[10px] font-bold block hover:bg-blue-700"
                        >
                          เปิดดูสลิปใบเสร็จรับเงิน
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Flex Message Payload & Integration Info */}
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
                  <h4 className="text-xs font-bold text-slate-800 mb-2">
                    การเชื่อมต่อ LINE Messaging API
                  </h4>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    ระบบจะส่ง JSON Flex Message นี้ผ่าน Webhook ไปยัง LINE Admin Group
                    ทันทีที่มีการส่งคำสั่งซื้อใหม่ พร้อมลิงก์รูปสลิปจาก Google Cloud Storage (GCS)
                  </p>
                </div>

                <div className="bg-slate-900 text-slate-200 p-4 rounded-xl text-[11px] font-mono overflow-auto max-h-[300px]">
                  <pre>{JSON.stringify({
                    type: 'flex',
                    altText: `คำสั่งซื้อใหม่ #${selectedOrderForLine.orderId}`,
                    orderId: selectedOrderForLine.orderId,
                    customer: selectedOrderForLine.customerName,
                    amount: selectedOrderForLine.totalAmount,
                    itemsCount: selectedOrderForLine.items.length,
                    gcsSlipUrl: selectedOrderForLine.slipUrl || 'gs://giffarine-payment-slips/...',
                  }, null, 2)}</pre>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
